import type {
  ClientDetailQuery,
  Currency,
  CurrencyExchangeRate,
  PipedriveCustomFieldValue,
  WorkspaceCompanyFragment,
} from "@src/__generated__/urql-graphql";
import { appStore } from "@src/stores/AppStore";
import { NumberModel } from "@src/utils/models";
import { action, computed, makeObservable, observable } from "mobx";

type TClient = NonNullable<ClientDetailQuery["client"]>;

type TContact = TClient["contacts"][0];

type TBillingEntity = TClient["billingEntities"][0];

type TExchangeRate = TClient["currencyExchangeRatesStructured"][0];

type TBrand = TClient["brands"][0];
type TPipedriveCustomFieldValue = Pick<PipedriveCustomFieldValue, "value"> & {
  customField: Pick<PipedriveCustomFieldValue["customField"], "label">;
};
type TBrandWithPipedriveCustomFieldValues = Omit<
  TBrand,
  "pipedriveCustomFieldValues"
> & {
  pipedriveCustomFieldValues?: TPipedriveCustomFieldValue[];
};
type TContactWithPipedriveCustomFieldValues = TContact & {
  pipedriveCustomFieldValues?: TPipedriveCustomFieldValue[];
};

export class ClientModel {
  id: string;
  @observable image?: TClient["image"];
  @observable name: string;
  @observable brands: BrandModel[] = [];
  @observable billingEntities: BillingEntityModel[] = [];
  @observable workspaceCompany: WorkspaceCompanyFragment;
  @observable.ref cloudUrl: TClient["cloudUrl"];
  @observable.ref driveCloudUrl: TClient["driveCloudUrl"];

  constructor(src: TClient) {
    makeObservable(this);
    this.id = src.id;
    this.image = src.image || undefined;
    this.name = src.name;
    this.brands = src.brands.map((i) => new BrandModel(i));
    this.billingEntities = src.billingEntities.map(
      (i) => new BillingEntityModel(i),
    );
    this.workspaceCompany = src.workspaceCompany;
    this.cloudUrl = src.cloudUrl;
    this.driveCloudUrl = src.driveCloudUrl;
  }
}

export class BrandModel {
  id: TBrand["id"];
  @observable name: TBrand["name"];
  @observable archived_at: TBrand["archived_at"];
  @observable code: TBrand["code"];
  @observable image: TBrand["image"];
  @observable defaultBillingEntity: TBrand["defaultBillingEntity"];
  @observable.ref pipedriveCustomFieldValues: TPipedriveCustomFieldValue[] = [];

  constructor(src: TBrandWithPipedriveCustomFieldValues) {
    makeObservable(this);
    this.id = src.id;
    this.name = src.name;
    this.archived_at = src.archived_at;
    this.code = src.code;
    this.image = src.image;
    this.defaultBillingEntity = src.defaultBillingEntity;
    this.pipedriveCustomFieldValues =
      src.pipedriveCustomFieldValues?.map((item) => ({
        value: item.value,
        customField: {
          label: item.customField.label,
        },
      })) ?? [];
  }

  @computed get defaultBillingEntityId(): string | undefined {
    return this.defaultBillingEntity?.id;
  }
}

export class BillingEntityModel {
  id: string;
  @observable internal_name: string;

  @observable legal_name: string;
  @observable street: string;
  @observable city: string;
  @observable zip: string;
  @observable registration_no?: string;
  @observable tax_no?: string;
  @observable vat_no?: string;
  @observable applies_vat: boolean;
  @observable payment_due: number;
  @observable invoice_email?: string;
  @observable note?: string;
  @observable country: TBillingEntity["country"];
  @observable language: TBillingEntity["language"];
  @observable defaultCurrency: TBillingEntity["defaultCurrency"];
  @observable workspaceCompany: TBillingEntity["workspaceCompany"];
  @observable workspaceBankAccount: TBillingEntity["workspaceBankAccount"];
  @observable disable_email_notification: boolean;

  constructor(src: TBillingEntity) {
    makeObservable(this);
    this.id = src.id;
    this.internal_name = src.internal_name;
    this.legal_name = src.legal_name;
    this.street = src.street;
    this.city = src.city;
    this.zip = src.zip;
    this.registration_no = src.registration_no ?? undefined;
    this.tax_no = src.tax_no ?? undefined;
    this.vat_no = src.vat_no ?? undefined;
    this.applies_vat = src.applies_vat;
    this.payment_due = src.payment_due;
    this.invoice_email = src.invoice_email ?? undefined;
    this.note = src.note ?? undefined;
    this.country = src.country;
    this.language = src.language;
    this.defaultCurrency = src.defaultCurrency;
    this.workspaceCompany = src.workspaceCompany;
    this.workspaceBankAccount = src.workspaceBankAccount;
    this.disable_email_notification = src.disable_email_notification;
  }
}

export class ContactModel {
  id: string;
  @observable first_name: string;
  @observable last_name: string;
  @observable email: string;
  @observable phone: string;
  @observable.ref pipedriveCustomFieldValues: TPipedriveCustomFieldValue[] = [];

  constructor(src: TContactWithPipedriveCustomFieldValues) {
    makeObservable(this);
    this.id = src.id;
    this.first_name = src.first_name ?? "";
    this.last_name = src.last_name ?? "";
    this.email = src.email ?? "";
    this.phone = src.phone ?? "";
    this.pipedriveCustomFieldValues =
      src.pipedriveCustomFieldValues?.map((item) => ({
        value: item.value,
        customField: {
          label: item.customField.label,
        },
      })) ?? [];
  }

  @computed get fullName() {
    return `${this.first_name} ${this.last_name}`;
  }
}

export class ExchangeRateModel {
  id: string;
  @observable.ref rate: NumberModel;
  @observable.ref valid_from?: Date;
  @observable.ref valid_to?: Date;

  @observable currency: TExchangeRate["currency"];

  constructor(
    rate: Pick<
      CurrencyExchangeRate,
      "id" | "exchange_rate" | "valid_from" | "valid_to"
    >,
    currency: Currency,
  ) {
    makeObservable(this);
    this.id = rate.id;
    this.currency = currency;
    this.rate = new NumberModel(
      rate.exchange_rate,
      appStore.workspaceStore.settings?.exchange_rate_denomination!,
    );
    if (rate.valid_from) this.valid_from = new Date(rate.valid_from);
    if (rate.valid_to) this.valid_to = new Date(rate.valid_to);
  }

  @action
  update(
    src: Pick<
      CurrencyExchangeRate,
      "id" | "exchange_rate" | "valid_from" | "valid_to"
    >,
  ) {
    this.rate = this.rate.setDenomValue(src.exchange_rate);
    this.valid_from = src.valid_from ? new Date(src.valid_from) : undefined;
    this.valid_to = src.valid_to ? new Date(src.valid_to) : undefined;
  }
}

export class CurrencyModel {
  @observable currency: TExchangeRate["currency"];
  @observable rates: ExchangeRateModel[];
  constructor(currency: TExchangeRate["currency"], rates: ExchangeRateModel[]) {
    makeObservable(this);
    this.currency = currency;
    this.rates = rates;
  }
}

type TActiveExchangeRate = NonNullable<
  ClientDetailQuery["client"]
>["currencyExchangeRates"][0];

export class ActiveExchangeRateModel {
  id: string;
  @observable currency: TActiveExchangeRate["currency"];
  @observable.ref rate: NumberModel;

  constructor(src: TActiveExchangeRate) {
    makeObservable(this);
    this.currency = src.currency;
    this.id = src.id;
    this.rate = NumberModel.fromDenomValue(
      src.exchange_rate,
      appStore.workspaceStore.settings?.exchange_rate_denomination!,
    );
  }
}
